import { useSyncExternalStore } from 'react'

import { notifyStorageKeyChange, subscribeToStorageKey } from '@/utils/storage'

/**
 * Shared storage keys + helpers for AppShell's "sticky network/view
 * selection": when the user navigates to a global route that doesn't
 * carry a networkId (e.g. /optimization-jobs, /performance), the
 * shell still surfaces the most-recently-visited network so the user
 * doesn't lose their place.
 *
 * AppShell writes to these on every URL transition; HomePage reads
 * them on mount to seed the inline browser's highlighted row.
 *
 * Keep this module's API minimal — only the keys and the lazy
 * read/write helpers belong here.
 */

export const LAST_NETWORK_ID_STORAGE_KEY = 'kbe.lastNetworkId'
export const LAST_VIEW_ID_STORAGE_KEY = 'kbe.lastViewId'

export const readStored = (key: string): string | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    return localStorage.getItem(key) ?? undefined
  } catch {
    return undefined
  }
}

export const writeStored = (key: string, value: string | undefined): void => {
  if (typeof window === 'undefined') return
  try {
    if (value) localStorage.setItem(key, value)
    else localStorage.removeItem(key)
  } catch {
    // ignore
  }
  notifyStorageKeyChange(key)
}

const rejectedViewPairs = new Set<string>()

const viewPairKey = (networkId: string, viewId: string): string =>
  `${networkId}:${viewId}`

/**
 * Records that a view cannot load under a given network — it was deleted, or it
 * belongs to a different network.
 *
 * Recording the *pair* is what makes this safe: the same view is perfectly
 * valid under the network it belongs to, and a view-id-only rule would both
 * block it there and wipe a legitimately stored selection for that network.
 *
 * This deliberately does not touch storage. The stored view id carries no
 * network of its own, so only AppShell — which knows the network currently in
 * play — can decide whether the stored id is the rejected one; it owns both the
 * clear and the skip-persist (without the latter the clear would be undone
 * immediately, since the URL still carries the bad id when we reject it).
 * Rejecting notifies the readers below so that decision runs now (KBE-718).
 */
export const rejectStickyView = (networkId: string, viewId: string): void => {
  rejectedViewPairs.add(viewPairKey(networkId, viewId))
  rejectionListeners.forEach((listener) => listener())
}

export const isRejectedStickyView = (
  networkId: string,
  viewId: string,
): boolean => rejectedViewPairs.has(viewPairKey(networkId, viewId))

const rejectionListeners = new Set<() => void>()

const subscribeToRejections = (onChange: () => void): (() => void) => {
  rejectionListeners.add(onChange)
  return () => rejectionListeners.delete(onChange)
}

/**
 * Reactive form of `isRejectedStickyView`.
 *
 * The rejection registry needs its own subscription rather than riding on the
 * storage keys': a rejection does not change the stored *string*, so
 * `useStoredValue`'s snapshot stays `Object.is`-equal and `useSyncExternalStore`
 * correctly bails out of re-rendering. Notifying that key therefore reached
 * nobody, and AppShell went on using the rejected pair until some unrelated
 * render happened to recompute it.
 */
export const useIsRejectedStickyView = (
  networkId: string | undefined,
  viewId: string | undefined,
): boolean =>
  useSyncExternalStore(
    subscribeToRejections,
    () =>
      !!networkId &&
      !!viewId &&
      rejectedViewPairs.has(viewPairKey(networkId, viewId)),
    () => false,
  )

const getServerSnapshot = (): undefined => undefined

/**
 * Reactive read of a sticky-selection storage key: `undefined` on the
 * server and on the first client render (matching SSR), the stored value
 * once mounted. Uses `useSyncExternalStore` instead of a `useEffect` +
 * `setState` mount-hydration pair (react-hooks/set-state-in-effect) --
 * writes made anywhere via `writeStored` notify every subscribed reader, so
 * AppShell and HomePage (both readers of the same keys) stay in sync.
 */
export const useStoredValue = (key: string): string | undefined =>
  useSyncExternalStore(
    (onChange) => subscribeToStorageKey(key, onChange),
    () => readStored(key),
    getServerSnapshot,
  )
